// src/pages/platform-admin/PlatformProfile.js

import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Avatar,
    Button,
    TextField,
    Grid,
    Divider,
    IconButton,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Chip,
    Fade,
    Zoom,
    Tooltip,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
    Edit,
    Save,
    Cancel,
    Person,
    Email,
    Badge,
    CheckCircle,
    Warning,
    Timeline,
    Dashboard,
    AdminPanelSettings,
    VerifiedUser,
    Info,
    Lock,
    Visibility,
    VisibilityOff,
    PhotoCamera,
    Delete
} from '@mui/icons-material';
import useCustomToast from '../../hooks/useToast';
import { authService } from '../../apiServices/authService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import PlatformAdminBar from '../../components/PlatformAdminBar';
import { APIUserProfileById, APIUserProfileImage, APIUserUpdate, APIUserUpdatePassword } from '../../apiServices/APIs';
import { getImageUrl, getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import { defaultUserIcon } from '../../assets';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const pulseAnimation = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
`;

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
    width: 150,
    height: 150,
    margin: '0 auto',
    border: `4px solid ${theme.palette.primary.main}`,
    boxShadow: theme.shadows[3],
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'scale(1.02)',
        boxShadow: theme.shadows[6]
    },
    [theme.breakpoints.down('sm')]: {
        width: 120,
        height: 120,
    }
}));

const StyledCard = styled(Card)(({ theme }) => ({
    transition: 'all 0.3s ease',
    height: '100%',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[8]
    }
}));


// Helper function to get initials from user name
const getInitials = (name) => {
    if (!name) return '?';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

// Helper function to get avatar color based on user name
const stringToColor = (string) => {
    if (!string) return '#1976d2';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

const PlatformProfile = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        status: '',
        email: '',
        role: '',
    });
    const [passwordForm, setPasswordForm] = useState({
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
    });
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [profileImageUrl, setProfileImageUrl] = useState(null);
    const [uploading, setUpdating] = useState(false);
    const showToast = useCustomToast();

    useEffect(() => {
        apiService.setToastFunction(showToast);
    }, [showToast]);
    

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        setLoading(true);
        try {
            const currentUserId = getUserDetails("Id");
            const result = await apiService.show(APIUserProfileById, currentUserId);
            if (result.success) {
                setProfile(result.data);
                setFormData({
                    name: result.data.name || '',
                    email: result.data.email || '',
                    status: result.data.status || '',
                    role: result.data.role || ''
                });
                const imgUrl = getImageUrl (APIUserProfileImage, result.data.profile_image_path);
                setProfileImageUrl(imgUrl || defaultUserIcon);
            } else {
                showToast('Error', result.message || 'Failed to load profile', 'error');
            }
        } catch (error) {
            showToast('Error', 'Failed to load profile', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordForm(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showToast('Error', 'Image size must be less than 5MB', 'error');
                return;
            }
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
    };

    const handleSave = async () => {
        setUpdating(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('email', formData.email);
            formDataToSend.append('name', formData.name);
            formDataToSend.append('role', formData.role);
            formDataToSend.append('status', formData.status);
            formDataToSend.append('updateId', getUserDetails("Id"));

            if (selectedImage) {
                formDataToSend.append('profile_image', selectedImage);
            }
            const result = await apiService.update(APIUserUpdate, formDataToSend);
            if (result.success) {
                showToast('Success', 'Profile updated successfully', 'success');

                // Update sessionStorage with new user data
                const currentUser = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
                const updatedUser = {
                    ...currentUser,
                    name: formData.name,
                    email: formData.email,
                    role: formData.role,
                    status: formData.status,
                    profile_image_path: result.data?.profile_image_path || currentUser.profile_image_path
                };
                sessionStorage.setItem("userInfo", JSON.stringify(updatedUser));
                setEditing(false);
                setSelectedImage(null);
                setImagePreview(null);
                // Refresh profile data
                await fetchProfile();
            } else {
                showToast('Error', result.message || 'Failed to update profile', 'error');
            }
        } catch (error) {
            console.error("Update error:", error);
            showToast('Error', 'Failed to update profile', 'error');
        } finally {
            setUpdating(false);
        }
    };

    const handleUpdatePassword = async () => {
        if (passwordForm.new_password !== passwordForm.new_password_confirmation) {
            showToast('Error', 'New passwords do not match', 'error');
            return;
        }

        if (passwordForm.new_password.length < 4) {
            showToast('Error', 'Password must be at least 4 characters', 'error');
            return;
        }

        setUpdating(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('current_password', passwordForm.current_password);
            formDataToSend.append('new_password', passwordForm.new_password);
            formDataToSend.append('updateId', getUserDetails("Id"));
            const result = await apiService.update(APIUserUpdatePassword, formDataToSend);

            if (result.success) {
                showToast('Success', 'Password updated successfully', 'success');
                setOpenPasswordDialog(false);
                setPasswordForm({
                    current_password: '',
                    new_password: '',
                    new_password_confirmation: ''
                });
                await fetchProfile();
            } else {
                showToast('Error', result.message || 'Failed to update password', 'error');
            }
        } catch (error) {
            console.log("error" + error);
            showToast('Error', 'Failed to update password', 'error');
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return <LoadingState message="Loading Profile..." variant="research" size="large" fullPage />;
    }

    if (!profile) {
        return <ErrorState error="Failed to load profile" onRetry={fetchProfile} title="Unable to Load Profile" variant="research" fullPage retryText="Retry Loading" />;
    }

    // Get avatar color and initials
    const avatarColor = stringToColor(profile.name);
    const initials = getInitials(profile.name);

    return (
        <>
            <FullWidthContainer >
                <Fade in={true} timeout={800}>
                    <Box>
                        <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                            <PlatformAdminBar  notificationCount={89} />
                            {/* Header Section */}
                            <Paper elevation={2} sx={{ 
                                p: { xs: 2, sm: 3 }, 
                                mb: { xs: 2, sm: 3 }, 
                                borderRadius: 3,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                color: 'white'
                            }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    flexWrap: 'wrap', 
                                    gap: 2,
                                    flexDirection: { xs: 'column', sm: 'row' }
                                }}>
                                    <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold', 
                                            mb: 1,
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            Welcome, {getUserDetails("userName")}!
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            Manage your platform admin profile
                                        </Typography>
                                    </Box>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: 1,
                                        justifyContent: { xs: 'center', sm: 'flex-end' },
                                        flexWrap: 'wrap'
                                    }}>
                                        <Chip 
                                            icon={profile.status === '1' ? <CheckCircle /> : <Warning />}
                                            label={profile.status === '1' ? 'Active Account' : 'Inactive Account'}
                                            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
                                            size={isMobile ? "small" : "medium"}
                                        />
                                        <Chip 
                                            icon={<AdminPanelSettings />}
                                            label="Platform Administrator"
                                            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
                                            size={isMobile ? "small" : "medium"}
                                        />
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>
                        
                        {/* Main Profile Card */}
                        <Paper elevation={3} sx={{ 
                            p: { xs: 2, sm: 3, md: 4 }, 
                            borderRadius: 3,
                            backgroundColor: 'white'
                        }}>
                            {/* Profile Header */}
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                    {imagePreview ? (
                                        <ProfileAvatar 
                                            src={imagePreview} 
                                            alt={profile?.name}
                                            sx={{ 
                                                animation: editing ? `${pulseAnimation} 1.5s ease-in-out infinite` : 'none',
                                                cursor: editing ? 'pointer' : 'default'
                                            }}
                                        />
                                    ) : profileImageUrl ? (
                                        <ProfileAvatar 
                                            src={profileImageUrl} 
                                            alt={profile?.name}
                                            sx={{ 
                                                animation: editing ? `${pulseAnimation} 1.5s ease-in-out infinite` : 'none',
                                                cursor: editing ? 'pointer' : 'default'
                                            }}
                                        />
                                    ) : (
                                        <ProfileAvatar 
                                            sx={{ 
                                                bgcolor: avatarColor,
                                                animation: editing ? `${pulseAnimation} 1.5s ease-in-out infinite` : 'none',
                                                cursor: editing ? 'pointer' : 'default'
                                            }}
                                        >
                                            <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                                                {initials}
                                            </Typography>
                                        </ProfileAvatar>
                                    )}
                                    {editing && (
                                        <Box sx={{ position: 'absolute', bottom: 0, right: 0, display: 'flex', gap: 1 }}>
                                            <Tooltip title="Upload Photo">
                                                <IconButton
                                                    component="label"
                                                    sx={{
                                                        backgroundColor: 'primary.main',
                                                        color: 'white',
                                                        '&:hover': { backgroundColor: 'primary.dark' },
                                                        width: { xs: 32, sm: 40 },
                                                        height: { xs: 32, sm: 40 }
                                                    }}
                                                >
                                                    <PhotoCamera fontSize="small" />
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept="image/jpeg,image/png,image/jpg,image/gif"
                                                        onChange={handleImageChange}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                            {(selectedImage || profileImageUrl) && (
                                                <Tooltip title="Remove Photo">
                                                    <IconButton
                                                        onClick={handleRemoveImage}
                                                        sx={{
                                                            backgroundColor: 'error.main',
                                                            color: 'white',
                                                            '&:hover': { backgroundColor: 'error.dark' },
                                                            width: { xs: 32, sm: 40 },
                                                            height: { xs: 32, sm: 40 }
                                                        }}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                                <Typography variant="h5" sx={{ 
                                    mt: 2, 
                                    fontWeight: 'bold',
                                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                }}>
                                    {profile.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    Platform Administrator
                                </Typography>
                                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip 
                                        icon={<Email />} 
                                        label={profile.email} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    />
                                    <Chip 
                                        icon={<Badge />} 
                                        label={`Role: ${profile.role || 'admin'}`} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    />
                                </Box>
                            </Box>

                            <Divider sx={{ my: 3 }}>
                                <Chip label="Profile Information" icon={<Dashboard />} />
                            </Divider>

                            {/* Edit Mode Toggle */}
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'flex-end', 
                                mb: 3, 
                                gap: 2,
                                flexDirection: { xs: 'column', sm: 'row' }
                            }}>
                                {!editing ? (
                                    <>
                                        <Button
                                            variant="outlined"
                                            startIcon={<Lock />}
                                            onClick={() => setOpenPasswordDialog(true)}
                                            sx={{ borderRadius: 2 }}
                                            size={isMobile ? "small" : "medium"}
                                            fullWidth={isMobile}
                                        >
                                            Change Password
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={<Edit />}
                                            onClick={() => setEditing(true)}
                                            sx={{ borderRadius: 2 }}
                                            size={isMobile ? "small" : "medium"}
                                            fullWidth={isMobile}
                                        >
                                            Edit Profile
                                        </Button>
                                    </>
                                ) : (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: 2,
                                        width: { xs: '100%', sm: 'auto' }
                                    }}>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<Cancel />}
                                            onClick={() => {
                                                setEditing(false);
                                                setSelectedImage(null);
                                                setImagePreview(null);
                                                setFormData({
                                                    name: profile.name || '',
                                                    email: profile.email || '',
                                                    status: profile.status || '',
                                                    role: profile.role || ''
                                                });
                                            }}
                                            disabled={uploading}
                                            size={isMobile ? "small" : "medium"}
                                            sx={{ flex: { xs: 1, sm: 'none' } }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            startIcon={<Save />}
                                            onClick={handleSave}
                                            disabled={uploading}
                                            size={isMobile ? "small" : "medium"}
                                            sx={{ flex: { xs: 1, sm: 'none' } }}
                                        >
                                            {uploading ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </Box>
                                )}
                            </Box>

                            {/* View Mode Content */}
                            {!editing && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <StyledCard variant="outlined">
                                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                                <Typography variant="h6" gutterBottom sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 1,
                                                    fontSize: { xs: '1rem', sm: '1.125rem' }
                                                }}>
                                                    <Person color="primary" /> Personal Information
                                                </Typography>
                                                <Divider sx={{ mb: 2 }} />
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Person fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            <strong>Full Name:</strong> {profile.name}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Email fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            <strong>Email:</strong> {profile.email}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Badge fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            <strong>Role:</strong> {profile.role || 'Platform Administrator'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <VerifiedUser fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            <strong>Status:</strong> 
                                                            <Chip 
                                                                size="small" 
                                                                label={profile.status === '1' ? 'Active' : 'Inactive'}
                                                                color={profile.status === '1' ? 'success' : 'error'}
                                                                sx={{ ml: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                                            />
                                                        </Typography>
                                                    </Box>
                                                    {profileImageUrl && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                            <PhotoCamera fontSize="small" color="action" />
                                                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                <strong>Profile Photo:</strong> Uploaded
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </CardContent>
                                        </StyledCard>
                                    </Grid>

                                    {/* Account Information Card */}
                                    <Grid item xs={12}>
                                        <StyledCard variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                                <Typography variant="h6" gutterBottom sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 1,
                                                    fontSize: { xs: '1rem', sm: '1.125rem' }
                                                }}>
                                                    <VerifiedUser color="primary" /> Account Information
                                                </Typography>
                                                <Divider sx={{ mb: 2 }} />
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                            Email Verified
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            {profile.email_verified_at ? 
                                                                <><CheckCircle fontSize="small" color="success" /> Yes</> : 
                                                                <><Warning fontSize="small" color="warning" /> No</>
                                                            }
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                            Member Since
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            {new Date(profile.created_at).toLocaleDateString()}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                            Last Updated
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            {new Date(profile.updated_at).toLocaleDateString()}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </StyledCard>
                                    </Grid>
                                </Grid>
                            )}

                            {/* Edit Mode Content */}
                            {editing && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Full Name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            sx={{ mb: 3 }}
                                            size={isMobile ? "small" : "medium"}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Person color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            size={isMobile ? "small" : "medium"}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Email color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </Paper>
                    </Box>
                </Fade>

                {/* Change Password Dialog */}
                <Dialog 
                    open={openPasswordDialog} 
                    onClose={() => setOpenPasswordDialog(false)} 
                    maxWidth="sm" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Change Password
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                type={showPassword ? 'text' : 'password'}
                                label="Current Password"
                                name="current_password"
                                value={passwordForm.current_password}
                                onChange={handlePasswordChange}
                                sx={{ mb: 2 }}
                                size={isMobile ? "small" : "medium"}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size={isMobile ? "small" : "medium"}>
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                type={showConfirmPassword ? 'text' : 'password'}
                                label="New Password"
                                name="new_password"
                                value={passwordForm.new_password}
                                onChange={handlePasswordChange}
                                sx={{ mb: 2 }}
                                size={isMobile ? "small" : "medium"}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size={isMobile ? "small" : "medium"}>
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                type={showConfirmPassword ? 'text' : 'password'}
                                label="Confirm New Password"
                                name="new_password_confirmation"
                                value={passwordForm.new_password_confirmation}
                                onChange={handlePasswordChange}
                                size={isMobile ? "small" : "medium"}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size={isMobile ? "small" : "medium"}>
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={() => setOpenPasswordDialog(false)} size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button onClick={handleUpdatePassword} variant="contained" disabled={uploading} size={isMobile ? "small" : "medium"}>
                            {uploading ? <CircularProgress size={24} /> : 'Update Password'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default PlatformProfile;